/**
 * ClientNotificationsEmbed — embedded version of Notifications for the portal tab.
 * Shows real notifications from Supabase filtered for the current client & project.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { timeAgoKey, translateNotificationTitle, translateNotificationBody } from '@/lib/dateUtils';
import { resolveNotificationRoute } from '@/lib/notificationRoutes';
import type { AuthProfile } from '@/hooks/useAuth';

interface NotificationItem {
  id: string;
  project_id: string | null;
  project_name: string | null;
  notification_type: string;
  title: string;
  body: string;
  target_role: string | null;
  target_type?: string | null;
  link_path: string | null;
  related_table?: string | null;
  is_read: boolean;
  created_at: string;
}

const typeConfig: Record<string, { icon: string; color: string }> = {
  progress:    { icon: 'ri-checkbox-circle-line', color: '#059669' },
  issue:       { icon: 'ri-error-warning-line',    color: '#EF4444' },
  inspection:  { icon: 'ri-shield-check-line',     color: '#059669' },
  approval:    { icon: 'ri-file-list-3-line',      color: '#E8621A' },
  question:    { icon: 'ri-question-answer-line',  color: '#E8621A' },
  task:        { icon: 'ri-task-line',             color: '#F59E0B' },
  system:      { icon: 'ri-information-line',      color: '#9CA3AF' },
  payment:     { icon: 'ri-bank-card-line',        color: '#059669' },
  update:      { icon: 'ri-megaphone-line',        color: '#E8621A' },
  photo:       { icon: 'ri-image-line',            color: '#0891B2' },
  file:        { icon: 'ri-folder-upload-line',     color: '#7C3AED' },
  schedule:    { icon: 'ri-calendar-line',         color: '#F59E0B' },
  instruction: { icon: 'ri-file-list-3-line',      color: '#E8621A' },
  checklist:   { icon: 'ri-list-check-2',          color: '#374151' },
};

function getTypeConfig(type: string) {
  return typeConfig[type?.toLowerCase()] || { icon: 'ri-notification-3-line', color: '#9CA3AF' };
}

interface ClientNotificationsEmbedProps {
  userId: string | null;
  profile: AuthProfile | null;
  projectId: string | null;
}

export default function ClientNotificationsEmbed({ userId, profile, projectId }: ClientNotificationsEmbedProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<NotificationItem[]>([]);

  function timeAgo(iso: string): string {
    const { key, params } = timeAgoKey(iso);
    return t(key, params);
  }
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'All' | 'Unread'>('All');

  useEffect(() => {
    if (userId) loadNotifications();
  }, [userId, projectId]);

  async function loadNotifications() {
    setLoading(true);
    const role = profile?.user_type ?? 'client';

    const { data: notifs } = await supabase
      .from('jphph_notifications')
      .select('*')
      .or(`user_id.eq.${userId},user_id.is.null`)
      .eq('is_visible', true)
      .order('created_at', { ascending: false })
      .limit(100);

    if (!notifs) { setLoading(false); return; }

    const { data: reads } = await supabase
      .from('jphph_notification_reads')
      .select('notification_id')
      .eq('user_id', userId!);

    const readSet = new Set((reads || []).map((r) => r.notification_id));

    const enriched: NotificationItem[] = notifs
      .filter((n) => n.target_role === role || !n.target_role || n.user_id === userId)
      .filter((n) => !projectId || n.project_id === projectId || !n.project_id)
      .map((n) => ({
        ...n,
        is_read: readSet.has(n.id) || n.is_read,
      }));

    setItems(enriched);
    setLoading(false);
  }

  async function markRead(id: string) {
    if (!userId) return;
    await supabase.from('jphph_notification_reads').upsert(
      { notification_id: id, user_id: userId },
      { onConflict: 'notification_id,user_id' }
    );
    await supabase.from('jphph_notifications').update({ is_read: true, read_at: new Date().toISOString() }).eq('id', id);
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
  }

  async function markAllRead() {
    if (!userId) return;
    const unread = items.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    await supabase.from('jphph_notification_reads').upsert(
      unread.map((n) => ({ notification_id: n.id, user_id: userId })),
      { onConflict: 'notification_id,user_id' }
    );
    await supabase.from('jphph_notifications').update({ is_read: true, read_at: new Date().toISOString() }).in('id', unread.map((n) => n.id));
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  const handleClick = async (n: NotificationItem) => {
    if (!n.is_read) await markRead(n.id);
    const route = resolveNotificationRoute(n, 'client');
    if (route) navigate(route);
  };

  const unreadCount = items.filter((n) => !n.is_read).length;
  const filtered = activeFilter === 'All' ? items : items.filter((n) => !n.is_read);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {(['All', 'Unread'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeFilter === f ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
              }`}
            >
              {f === 'All' ? t('client.notifications.all') : t('client.notifications.unread')}
              {f === 'Unread' && unreadCount > 0 && (
                <span className="ml-1.5 bg-[#E8621A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            {t('client.notifications.markAllRead')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((n) => {
            const cfg = getTypeConfig(n.notification_type);
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left rounded-2xl border overflow-hidden cursor-pointer transition-all active:scale-[0.99] ${
                  n.is_read ? 'bg-white border-gray-100' : 'bg-white border-[#E8621A]/20'
                }`}
              >
                {!n.is_read && <div className="h-0.5 bg-[#E8621A]"></div>}

                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${cfg.color}15` }}
                    >
                      <i className={`${cfg.icon} text-base`} style={{ color: cfg.color }}></i>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className={`text-sm leading-snug flex-1 ${n.is_read ? 'text-[#374151] font-medium' : 'text-[#1A1A1A] font-semibold'}`}>
                          {translateNotificationTitle(n.notification_type, n.title, t)}
                        </p>
                        {!n.is_read && (
                          <div className="w-2 h-2 rounded-full bg-[#E8621A] flex-shrink-0 mt-1.5"></div>
                        )}
                      </div>

                      <p className="text-[#9CA3AF] text-xs leading-relaxed line-clamp-2 mb-2">
                        {translateNotificationBody(n.notification_type, n.body, t, i18n.language)}
                      </p>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        {n.project_name && (
                          <div className="flex items-center gap-1 bg-[#F0FDF4] px-2 py-0.5 rounded-full">
                            <i className="ri-building-2-line text-[9px] text-[#059669]"></i>
                            <span className="text-[10px] font-bold text-[#059669]">{n.project_name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 bg-[#FEF0E7] px-2 py-0.5 rounded-full">
                          <i className={`${cfg.icon} text-[9px] text-[#E8621A]`}></i>
                          <span className="text-[10px] font-bold text-[#E8621A] capitalize">{n.notification_type}</span>
                        </div>
                        <span className="text-[#D1D5DB] text-[10px] ml-auto">{timeAgo(n.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-notification-3-line text-5xl"></i>
          </div>
          <div className="text-[#374151] font-semibold mb-1">
            {activeFilter === 'Unread' ? t('client.notifications.allCaughtUp') : t('client.notifications.noNotifications')}
          </div>
          <div className="text-[#9CA3AF] text-sm">
            {activeFilter === 'Unread' ? t('client.notifications.noUnread') : t('client.notifications.noNotificationsDesc')}
          </div>
        </div>
      )}

      <button
        onClick={() => navigate('/client/notifications')}
        className="w-full py-3 rounded-xl bg-[#1A1A1A] text-white text-sm font-semibold cursor-pointer"
      >
        {t('embed.notifications.viewAll')}
      </button>
    </div>
  );
}
